import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useBadges, type BadgeView } from "@/hooks/useBadges";
import {
  useShoutOutConnectors,
  useCareerClimbsContent,
  useCoursesContent,
  useAwards,
  usePathways,
} from "@/hooks/useCMSData";
import { useHatThemes } from "@/hooks/useHatThemes";
import { useCelebrationProcessor } from "@/components/celebration/CelebrationCoordinator";
import { ShoutOutSection } from "@/components/celebration/sections/ShoutOutSection";
import { HatPackSection } from "@/components/celebration/sections/HatPackSection";
import { CareerClimbSection } from "@/components/celebration/sections/CareerClimbSection";
import { CourseSection } from "@/components/celebration/sections/CourseSection";
import { CertificateSection } from "@/components/celebration/sections/CertificateSection";
import { ClusterSection } from "@/components/celebration/sections/ClusterSection";
import { BadgeDetailContent } from "@/pages/middle-school/ms-climber-profile-badges/BadgeDetailModal";
import type {
  CelebrationItem,
  ShadeProcessor,
  ShadeRenderContext,
} from "@/components/celebration/types";
import type {
  CMSAward,
  CMSCareerClimb,
  CMSCareerCluster,
  CMSCourse,
  CMSHatTheme,
  CMSShoutOutConnector,
} from "@/types/cms-types";
import type { MSShoutOut } from "@/hooks/useCreateShoutOut";

type Kind =
  | "badge"
  | "shoutout"
  | "hat-pack"
  | "climb"
  | "course"
  | "certificate"
  | "cluster";

type SelectedItem = {
  kind: Kind;
  id: string;
  label: string;
  payload: unknown;
};

const PRIORITY: Record<Kind, number> = {
  shoutout: 10,
  climb: 20,
  course: 25,
  cluster: 30,
  certificate: 40,
  badge: 50,
  "hat-pack": 60,
};

const keyFor = (kind: Kind, id: string) => `${kind}:${id}`;

const buildShoutOutPayload = (connector: CMSShoutOutConnector): MSShoutOut => ({
  id: `debug-shoutout-${connector.id}`,
  path: "debug",
  parentID: "debug",
  climberID: "debug",
  connectorId: connector.id,
  message: `Debug shout-out for "${connector.name}". This is preview text.`,
  createdByUserId: "debug",
  createdByType: "debug",
  createdByName: "Debug Teacher",
  createdAt: new Date().toISOString(),
  acknowledged: false,
});

export const CelebrationsDebug = () => {
  const { badges = [], loading: badgesLoading } = useBadges() as {
    badges: BadgeView[];
    loading: boolean;
  };
  const { connectors, loading: shoutOutsLoading } = useShoutOutConnectors();
  const { allThemes: hatThemes, loading: hatThemesLoading } =
    useHatThemes() as {
      allThemes: CMSHatTheme[];
      loading: boolean;
    };
  const { careerClimbs, loading: climbsLoading } = useCareerClimbsContent();
  const { courses, loading: coursesLoading } = useCoursesContent();
  const { awards, loading: awardsLoading } = useAwards();
  const { clusters, loading: pathwaysLoading } = usePathways();

  const anyLoading =
    badgesLoading ||
    shoutOutsLoading ||
    hatThemesLoading ||
    climbsLoading ||
    coursesLoading ||
    awardsLoading ||
    pathwaysLoading;

  // Single config drives sorting, group rendering, and the item lookup map
  // for every celebration kind. Each entry describes a source array and how
  // to project one of its records into a SelectedItem.
  const { groups, itemsByKey } = useMemo(() => {
    type GroupConfig<T> = {
      kind: Kind;
      title: string;
      source: T[];
      label: (item: T) => string;
      payload: (item: T) => unknown;
    };
    const configs: GroupConfig<any>[] = [
      {
        kind: "badge",
        title: "Badges",
        source: badges,
        label: (b: BadgeView) => b.name,
        payload: (b: BadgeView) => b,
      },
      {
        kind: "shoutout",
        title: "Shout-outs",
        source: connectors,
        label: (c: CMSShoutOutConnector) => c.name,
        payload: (c: CMSShoutOutConnector) => buildShoutOutPayload(c),
      },
      {
        kind: "hat-pack",
        title: "Hat Packs",
        source: hatThemes,
        label: (t: CMSHatTheme) => t.name,
        payload: (t: CMSHatTheme) => t,
      },
      {
        kind: "climb",
        title: "Career Climbs",
        source: careerClimbs,
        label: (c: CMSCareerClimb) => c.name,
        payload: (c: CMSCareerClimb) => c,
      },
      {
        kind: "course",
        title: "Courses",
        source: courses,
        label: (c: CMSCourse) => c.name,
        payload: (c: CMSCourse) => c,
      },
      {
        kind: "certificate",
        title: "Certificates",
        source: awards.filter((a) => a.awardType === "certificate"),
        label: (a: CMSAward) => a.title || a.name,
        payload: (a: CMSAward) => a,
      },
      {
        kind: "cluster",
        title: "Clusters",
        source: clusters.map((c) => c.cluster),
        label: (c: CMSCareerCluster) => c.name,
        payload: (c: CMSCareerCluster) => c,
      },
    ];

    const map = new Map<string, SelectedItem>();
    const built = configs.map((cfg) => {
      const sorted = [...cfg.source].sort((a, b) =>
        cfg.label(a).localeCompare(cfg.label(b)),
      );
      const keys = sorted.map((item) => {
        const key = keyFor(cfg.kind, item.id);
        map.set(key, {
          kind: cfg.kind,
          id: item.id,
          label: cfg.label(item),
          payload: cfg.payload(item),
        });
        return key;
      });
      return { kind: cfg.kind, title: cfg.title, keys };
    });
    return { groups: built, itemsByKey: map };
  }, [badges, connectors, hatThemes, careerClimbs, courses, awards, clusters]);

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [triggered, setTriggered] = useState<SelectedItem[] | null>(null);
  const [runId, setRunId] = useState(0);

  const toggle = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const setGroup = useCallback((keys: string[], on: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  }, []);

  const onTrigger = useCallback(() => {
    const picked: SelectedItem[] = [];
    for (const key of selectedKeys) {
      const item = itemsByKey.get(key);
      if (item) picked.push(item);
    }
    if (picked.length === 0) return;
    setTriggered(picked);
    setRunId((n) => n + 1);
  }, [selectedKeys, itemsByKey]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
      <Typography variant="h5">Celebrations Debug</Typography>
      <Typography variant="body2" color="text.secondary">
        Pick any combination and trigger them through the
        CelebrationCoordinator. Acknowledge APIs are NOT called — no badge / hat
        pack / shout-out / award / course / climb writes occur.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          onClick={onTrigger}
          disabled={selectedKeys.size === 0 || anyLoading}
        >
          Trigger {selectedKeys.size} celebration
          {selectedKeys.size === 1 ? "" : "s"}
        </Button>
        <Button
          variant="outlined"
          onClick={() => setSelectedKeys(new Set())}
          disabled={selectedKeys.size === 0}
        >
          Clear selection
        </Button>
        {anyLoading && (
          <Typography variant="body2" color="text.secondary">
            Loading CMS content…
          </Typography>
        )}
      </Stack>

      {groups.map((group) => {
        const allOn =
          group.keys.length > 0 && group.keys.every((k) => selectedKeys.has(k));
        return (
          <Paper key={group.kind} sx={{ p: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">
                {group.title} ({group.keys.length})
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  onClick={() => setGroup(group.keys, true)}
                  disabled={group.keys.length === 0 || allOn}
                >
                  Select all
                </Button>
                <Button
                  size="small"
                  onClick={() => setGroup(group.keys, false)}
                  disabled={group.keys.every((k) => !selectedKeys.has(k))}
                >
                  Clear
                </Button>
              </Stack>
            </Stack>
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr 1fr",
                },
                gap: 0.5,
              }}
            >
              {group.keys.map((key) => {
                const item = itemsByKey.get(key);
                if (!item) return null;
                return (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={selectedKeys.has(key)}
                        onChange={() => toggle(key)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">{item.label}</Typography>
                    }
                  />
                );
              })}
            </Box>
          </Paper>
        );
      })}

      {triggered && triggered.length > 0 && (
        <DebugCelebrationRegistrar items={triggered} runId={runId} />
      )}
    </Box>
  );
};

type DebugItemData = { kind: Kind; payload: unknown };

const renderDebugSection = (data: DebugItemData, ctx: ShadeRenderContext) => {
  switch (data.kind) {
    case "badge":
      return (
        <BadgeDetailContent
          badge={data.payload as BadgeView}
          animateEntrance={ctx.isActive}
          onClose={ctx.onAdvance}
          closeLabel={ctx.isLast ? "Close" : "Next"}
        />
      );
    case "shoutout":
      return (
        <ShoutOutSection
          shoutOut={data.payload as MSShoutOut}
          isActive={ctx.isActive}
          onAdvance={ctx.onAdvance}
          isLast={ctx.isLast}
        />
      );
    case "hat-pack":
      return (
        <HatPackSection
          hatPack={data.payload as CMSHatTheme}
          isActive={ctx.isActive}
          onAdvance={ctx.onAdvance}
          isLast={ctx.isLast}
        />
      );
    case "climb":
      return (
        <CareerClimbSection
          careerClimb={data.payload as CMSCareerClimb}
          isActive={ctx.isActive}
          onAdvance={ctx.onAdvance}
          isLast={ctx.isLast}
        />
      );
    case "course":
      return (
        <CourseSection
          course={data.payload as CMSCourse}
          isActive={ctx.isActive}
          onAdvance={ctx.onAdvance}
          isLast={ctx.isLast}
        />
      );
    case "certificate": {
      const award = data.payload as CMSAward;
      return (
        <CertificateSection
          award={{
            id: award.id,
            title: award.title,
            awardType: award.awardType,
            image: award.image
              ? { url: award.image.url, alt: award.image.alt ?? "" }
              : undefined,
            certificateConfig: award.certificateConfig,
          }}
          isActive={ctx.isActive}
          onAdvance={ctx.onAdvance}
          isLast={ctx.isLast}
        />
      );
    }
    case "cluster":
      return (
        <ClusterSection
          cluster={data.payload as CMSCareerCluster}
          onAdvance={ctx.onAdvance}
          isLast={ctx.isLast}
        />
      );
    default:
      return null;
  }
};

type DebugRegistrarProps = {
  items: SelectedItem[];
  runId: number;
};

const DebugCelebrationRegistrar = ({ items, runId }: DebugRegistrarProps) => {
  const celebrationItems = useMemo<CelebrationItem<DebugItemData>[]>(
    () =>
      items.map((s, i) => ({
        id: `debug:${runId}:${s.kind}:${s.id}:${i}`,
        priority: PRIORITY[s.kind],
        data: { kind: s.kind, payload: s.payload },
      })),
    [items, runId],
  );

  const renderItem = useCallback(
    (item: CelebrationItem, ctx: ShadeRenderContext) =>
      renderDebugSection(item.data as DebugItemData, ctx),
    [],
  );

  const onAcknowledge = useCallback(() => {
    // No-op: the debug screen MUST NOT call any real acknowledge API.
  }, []);

  const processor = useMemo<ShadeProcessor>(
    () => ({
      id: `debug-celebrations:${runId}`,
      mode: "shade",
      status: "ready",
      items: celebrationItems as CelebrationItem[],
      renderItem,
      onAcknowledge,
    }),
    [runId, celebrationItems, renderItem, onAcknowledge],
  );

  useCelebrationProcessor(processor);
  return null;
};

export default CelebrationsDebug;
